import Papa, { ParseResult } from 'papaparse';

export type ParsedCsv = {
  headers: string[];
  rows: { [key: string]: string }[];
};

const CANDIDATE_DELIMITERS = [',', ';', '\t', '|'];

const runPapaParse = (
  input: File | string,
  config: Papa.ParseConfig<{ [key: string]: string }>
): Promise<ParseResult<{ [key: string]: string }>> => {
  return new Promise((resolve, reject) => {
    Papa.parse(input as File, {
      ...config,
      complete: (results: ParseResult<{ [key: string]: string }>) => {
        resolve(results);
      },
      error: (error: Error) => {
        reject(error);
      },
    });
  });
};

const looksMisparsed = (results: ParseResult<{ [key: string]: string }>, delimiter: string) => {
  const fields = results.meta.fields ?? [];
  if (fields.length !== 1) return false;

  const hasQuoteError = results.errors.some((error) => error.type === 'Quotes');
  // A single header that still contains another candidate delimiter means the chosen
  // delimiter didn't match the file, so the whole row collapsed into one column.
  const hasOtherDelimiterInHeader = CANDIDATE_DELIMITERS.some((candidate) => {
    return candidate !== delimiter && fields[0].includes(candidate);
  });

  return hasQuoteError || hasOtherDelimiterInHeader;
};

export const parseCsv = async (input: File | string, delimiter: string): Promise<ParsedCsv> => {
  const firstResult = await runPapaParse(input, {
    header: true,
    skipEmptyLines: true,
    delimiter,
  });

  let results = firstResult;

  if (looksMisparsed(firstResult, delimiter)) {
    const rescueResult = await runPapaParse(input, {
      header: true,
      skipEmptyLines: true,
      delimitersToGuess: CANDIDATE_DELIMITERS,
    });

    const isRescueBetter =
      (rescueResult.meta.fields?.length ?? 0) > 1 &&
      !rescueResult.errors.some((error) => error.type === 'Quotes');

    if (isRescueBetter) {
      results = rescueResult;
    }
  }

  return { headers: results.meta.fields ?? [], rows: results.data ?? [] };
};
